import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getDonationTypes,
  createDonationType,
  updateDonationType,
  deleteDonationType,
  getLocations,
  createLocation,
  updateLocation,
  deleteLocation,
  getPackages,
  getPackage,
  createPackage,
  updatePackage,
  deletePackage,
  getTransactionFees,
  createTransactionFee,
  updateTransactionFee,
  deleteTransactionFee,
  getPackageCategories,
  createPackageCategory,
  updatePackageCategory,
  deletePackageCategory,
  getCampaigns,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  getDonationAmounts,
  createDonationAmount,
  updateDonationAmount,
  deleteDonationAmount,
  getDonationCurrencies,
  getCurrencies,
  createDonationCurrency,
  updateDonationCurrency,
  deleteDonationCurrency,
  getWebsiteSettings,
  upsertWebsiteSettings,
  getDonationLinks,
  createDonationLink,
  deleteDonationLink,
  updatePackageDisplayOrder
} from 'lib/supabaseQueries';
import { useAuthContext } from 'context/authContext';
import useBranchPermission from './useBranchPermission';
import { toast } from 'react-toastify';

// Hook to fetch donation types
export function useDonationTypes() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['donationTypes', organization?.id, branchId],
    queryFn: () => getDonationTypes(organization.id, branchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    donationTypes: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch locations
export function useLocations(type) {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['locations', organization?.id, branchId, type],
    queryFn: () => getLocations(organization.id, branchId, type),
    enabled: isInitialized && !!organization?.id
  });

  return {
    locations: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch packages with filters
export function usePackages(filters = {}) {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['packages', organization?.id, branchId, filters],
    queryFn: () => getPackages(organization.id, branchId, filters),
    enabled: isInitialized && !!organization?.id
  });

  return {
    packages: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch single package by ID
export function usePackage(id) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['package', id],
    queryFn: () => getPackage(id),
    enabled: !!id
  });

  return {
    package: data?.data || null,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch transaction fees
export function useTransactionFees() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['transactionFees', organization?.id, branchId],
    queryFn: () => getTransactionFees(organization.id, branchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    transactionFees: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch package categories
export function usePackageCategories() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['packageCategories', organization?.id, branchId],
    queryFn: () => getPackageCategories(organization.id, branchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    categories: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch campaigns
export function useCampaigns() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['campaigns', organization?.id, branchId],
    queryFn: () => getCampaigns(organization.id, branchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    campaigns: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch donation amounts
export function useDonationAmounts() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['donationAmounts', organization?.id, branchId],
    queryFn: () => getDonationAmounts(organization.id, branchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    donationAmounts: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hooks for donation type CRUD
export function useCreateDonationType() {
  const queryClient = useQueryClient();
  const { organization } = useAuthContext();
  const { branchId } = useBranchPermission();
  const mutation = useMutation({
    mutationFn: async data => {
      const result = await createDonationType({
        ...data,
        organisation_id: organization.id,
        branch_id: branchId
      });
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donationTypes'] });
      toast.success('Donation type created');
    },
    onError: error => {
      toast.error(error.message || 'Failed to create donation type');
    }
  });
  return {
    createDonationType: mutation.mutateAsync,
    isCreating: mutation.isPending
  };
}

export function useUpdateDonationType() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateDonationType(id, data);
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donationTypes'] });
      toast.success('Donation type updated');
    },
    onError: error => {
      toast.error(error.message || 'Failed to update donation type');
    }
  });
  return {
    updateDonationType: mutation.mutateAsync,
    isUpdating: mutation.isPending
  };
}

export function useDeleteDonationType() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteDonationType(id);
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donationTypes'] });
      toast.success('Donation type deleted');
    },
    onError: error => {
      toast.error(error.message || 'Failed to delete donation type');
    }
  });
  return {
    deleteDonationType: mutation.mutateAsync,
    isDeleting: mutation.isPending
  };
}

// Location CRUD hooks
export function useCreateLocation() {
  const queryClient = useQueryClient();
  const { organization } = useAuthContext();
  const { branchId } = useBranchPermission();
  const mutation = useMutation({
    mutationFn: async data => {
      const result = await createLocation({
        ...data,
        organisation_id: organization.id,
        branch_id: branchId
      });
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['locations'] });
      toast.success('Location created');
    },
    onError: error => {
      toast.error(error.message || 'Failed to create location');
    }
  });
  return {
    createLocation: mutation.mutateAsync,
    isCreating: mutation.isPending
  };
}

export function useUpdateLocation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateLocation(id, data);
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['locations'] });
      toast.success('Location updated');
    },
    onError: error => {
      toast.error(error.message || 'Failed to update location');
    }
  });
  return {
    updateLocation: mutation.mutateAsync,
    isUpdating: mutation.isPending
  };
}

export function useDeleteLocation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteLocation(id);
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['locations'] });
      toast.success('Location deleted');
    },
    onError: error => {
      toast.error(error.message || 'Failed to delete location');
    }
  });
  return {
    deleteLocation: mutation.mutateAsync,
    isDeleting: mutation.isPending
  };
}

// Campaign CRUD hooks
export function useCreateCampaign() {
  const queryClient = useQueryClient();
  const { organization } = useAuthContext();
  const { branchId } = useBranchPermission();
  const mutation = useMutation({
    mutationFn: async data => {
      const result = await createCampaign({
        ...data,
        organisation_id: organization.id,
        branch_id: branchId
      });
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Campaign created');
    },
    onError: error => {
      toast.error(error.message || 'Failed to create campaign');
    }
  });
  return {
    createCampaign: mutation.mutateAsync,
    isCreating: mutation.isPending
  };
}

export function useUpdateCampaign() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateCampaign(id, data);
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Campaign updated');
    },
    onError: error => {
      toast.error(error.message || 'Failed to update campaign');
    }
  });
  return {
    updateCampaign: mutation.mutateAsync,
    isUpdating: mutation.isPending
  };
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteCampaign(id);
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Campaign deleted');
    },
    onError: error => {
      toast.error(error.message || 'Failed to delete campaign');
    }
  });
  return {
    deleteCampaign: mutation.mutateAsync,
    isDeleting: mutation.isPending
  };
}

// Transaction Fee CRUD hooks
export function useCreateTransactionFee() {
  const queryClient = useQueryClient();
  const { organization } = useAuthContext();
  const { branchId } = useBranchPermission();
  const mutation = useMutation({
    mutationFn: async data => {
      const result = await createTransactionFee({
        ...data,
        organisation_id: organization.id,
        branch_id: branchId
      });
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['transactionFees'] });
      toast.success('Transaction fee created');
    },
    onError: error => {
      toast.error(error.message || 'Failed to create transaction fee');
    }
  });
  return {
    createTransactionFee: mutation.mutateAsync,
    isCreating: mutation.isPending
  };
}

export function useUpdateTransactionFee() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateTransactionFee(id, data);
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['transactionFees'] });
      toast.success('Transaction fee updated');
    },
    onError: error => {
      toast.error(error.message || 'Failed to update transaction fee');
    }
  });
  return {
    updateTransactionFee: mutation.mutateAsync,
    isUpdating: mutation.isPending
  };
}

export function useDeleteTransactionFee() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteTransactionFee(id);
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['transactionFees'] });
      toast.success('Transaction fee deleted');
    },
    onError: error => {
      toast.error(error.message || 'Failed to delete transaction fee');
    }
  });
  return {
    deleteTransactionFee: mutation.mutateAsync,
    isDeleting: mutation.isPending
  };
}

// Donation Amount CRUD hooks
export function useCreateDonationAmount() {
  const queryClient = useQueryClient();
  const { organization } = useAuthContext();
  const { branchId } = useBranchPermission();
  const mutation = useMutation({
    mutationFn: async data => {
      if (!branchId) {
        throw new Error(
          'Please select a specific branch to add donation amounts'
        );
      }
      const result = await createDonationAmount({
        ...data,
        organisation_id: organization.id,
        branch_id: branchId
      });
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donationAmounts'] });
      toast.success('Donation amount created');
    },
    onError: error => {
      toast.error(error.message || 'Failed to create donation amount');
    }
  });
  return {
    createDonationAmount: mutation.mutateAsync,
    isCreating: mutation.isPending
  };
}

export function useUpdateDonationAmount() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateDonationAmount(id, data);
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donationAmounts'] });
      toast.success('Donation amount updated');
    },
    onError: error => {
      toast.error(error.message || 'Failed to update donation amount');
    }
  });
  return {
    updateDonationAmount: mutation.mutateAsync,
    isUpdating: mutation.isPending
  };
}

export function useDeleteDonationAmount() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteDonationAmount(id);
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donationAmounts'] });
      toast.success('Donation amount deleted');
    },
    onError: error => {
      toast.error(error.message || 'Failed to delete donation amount');
    }
  });
  return {
    deleteDonationAmount: mutation.mutateAsync,
    isDeleting: mutation.isPending
  };
}

// Package Category CRUD hooks
export function useCreatePackageCategory() {
  const queryClient = useQueryClient();
  const { organization } = useAuthContext();
  const { branchId } = useBranchPermission();
  const mutation = useMutation({
    mutationFn: async data => {
      const result = await createPackageCategory({
        ...data,
        organisation_id: organization.id,
        branch_id: branchId
      });
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['packageCategories'] });
      toast.success('Package category created');
    },
    onError: error => {
      toast.error(error.message || 'Failed to create package category');
    }
  });
  return {
    createPackageCategory: mutation.mutateAsync,
    isCreating: mutation.isPending
  };
}

export function useUpdatePackageCategory() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updatePackageCategory(id, data);
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['packageCategories'] });
      toast.success('Package category updated');
    },
    onError: error => {
      toast.error(error.message || 'Failed to update package category');
    }
  });
  return {
    updatePackageCategory: mutation.mutateAsync,
    isUpdating: mutation.isPending
  };
}

export function useDeletePackageCategory() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deletePackageCategory(id);
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['packageCategories'] });
      toast.success('Package category deleted');
    },
    onError: error => {
      toast.error(error.message || 'Failed to delete package category');
    }
  });
  return {
    deletePackageCategory: mutation.mutateAsync,
    isDeleting: mutation.isPending
  };
}

// Hook to fetch donation currencies for an organization
export function useDonationCurrencies() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['donationCurrencies', organization?.id, branchId],
    queryFn: () => getDonationCurrencies(organization.id, branchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    donationCurrencies: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch all available currencies
export function useCurrencies() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['currencies'],
    queryFn: () => getCurrencies()
  });

  return {
    currencies: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Donation Currency CRUD hooks
export function useCreateDonationCurrency() {
  const queryClient = useQueryClient();
  const { organization } = useAuthContext();
  const { branchId } = useBranchPermission();
  const mutation = useMutation({
    mutationFn: async data => {
      const result = await createDonationCurrency({
        ...data,
        organisation_id: organization.id,
        branch_id: branchId
      });
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donationCurrencies'] });
      toast.success('Project currency created');
    },
    onError: error => {
      toast.error(error.message || 'Failed to create project currency');
    }
  });
  return {
    createDonationCurrency: mutation.mutateAsync,
    isCreating: mutation.isPending
  };
}

export function useUpdateDonationCurrency() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateDonationCurrency(id, data);
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donationCurrencies'] });
      toast.success('Project currency updated');
    },
    onError: error => {
      toast.error(error.message || 'Failed to update project currency');
    }
  });
  return {
    updateDonationCurrency: mutation.mutateAsync,
    isUpdating: mutation.isPending
  };
}

export function useDeleteDonationCurrency() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteDonationCurrency(id);
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donationCurrencies'] });
      toast.success('Project currency deleted');
    },
    onError: error => {
      toast.error(error.message || 'Failed to delete project currency');
    }
  });
  return {
    deleteDonationCurrency: mutation.mutateAsync,
    isDeleting: mutation.isPending
  };
}

// Hook to fetch website settings
export function useWebsiteSettings() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['websiteSettings', organization?.id, branchId],
    queryFn: () => getWebsiteSettings(organization.id, branchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    websiteSettings: data?.data || null,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Custom donation links (Website Settings)
export function useDonationLinks() {
  const { organization, isInitialized } = useAuthContext();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['donationLinks', organization?.id],
    queryFn: () => getDonationLinks(organization.id),
    enabled: isInitialized && !!organization?.id
  });

  return {
    donationLinks: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

export function useCreateDonationLink() {
  const queryClient = useQueryClient();
  const { organization, user } = useAuthContext();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async link => {
      const result = await createDonationLink({
        ...link,
        organisation_id: organization.id,
        branch_id: branchId || null,
        created_by: user?.id || null
      });
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donationLinks'] });
      toast.success('Link created');
    },
    onError: error => {
      toast.error(error.message || 'Failed to create link');
    }
  });

  return {
    createLink: mutation.mutate,
    createLinkAsync: mutation.mutateAsync,
    isCreating: mutation.isPending
  };
}

export function useDeleteDonationLink() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteDonationLink(id);
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['donationLinks'] });
      toast.success('Link deleted');
    },
    onError: error => {
      toast.error(error.message || 'Failed to delete link');
    }
  });

  return { deleteLink: mutation.mutate, isDeleting: mutation.isPending };
}

// Hook to upsert website settings
export function useUpsertWebsiteSettings() {
  const queryClient = useQueryClient();
  const { organization } = useAuthContext();
  const { branchId } = useBranchPermission();

  const mutation = useMutation({
    mutationFn: async data => {
      const result = await upsertWebsiteSettings({
        ...data,
        organisation_id: organization.id,
        branch_id: branchId
      });
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['websiteSettings'] });
      toast.success('Website settings updated');
    },
    onError: error => {
      toast.error(error.message || 'Failed to update website settings');
    }
  });
  return {
    upsertWebsiteSettings: mutation.mutateAsync,
    isUpdating: mutation.isPending
  };
}

// Package CRUD hooks
export function useCreatePackage() {
  const queryClient = useQueryClient();
  const { organization } = useAuthContext();
  const { branchId } = useBranchPermission();
  const mutation = useMutation({
    mutationFn: async data => {
      const result = await createPackage({
        ...data,
        organisation_id: organization.id,
        branch_id: branchId
      });
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['packages'] });
      toast.success('Package created successfully');
    },
    onError: error => {
      toast.error(error.message || 'Failed to create package');
    }
  });
  return {
    createPackage: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

export function useUpdatePackage() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updatePackage(id, data);
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['packages'] });
      toast.success('Package updated successfully');
    },
    onError: error => {
      toast.error(error.message || 'Failed to update package');
    }
  });
  return {
    updatePackage: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

export function useDeletePackage() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deletePackage(id);
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['packages'] });
      toast.success('Package deleted successfully');
    },
    onError: error => {
      toast.error(error.message || 'Failed to delete package');
    }
  });
  return {
    deletePackage: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update package display order
export function useUpdatePackageOrder() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (updates) => {
      const result = await updatePackageDisplayOrder(updates);
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['packages'] });
    },
    onError: error => {
      toast.error(error.message || 'Failed to update package order');
    }
  });
  return {
    updatePackageOrder: mutation.mutateAsync,
    isUpdating: mutation.isPending
  };
}
